import React, { Component } from 'react';
import { Container, Table, Button, Row, Badge, Alert, Modal, Form, Col } from 'react-bootstrap';
import { GoGitCommit, GoLock, GoTrash, GoPencil, GoUnlock, GoX, GoSync, GoHistory } from 'react-icons/go';
import ConfirmModal from 'components/controls/confirmModal';
import UserIdentity from 'components/controls/userIdentity';
import Clipboard from "blob_types/blobhub/compute/workflow/controls/common/clipboard";
import StatusIndicator from "components/controls/statusIndicator";
import Utils from "context/utils";
import { retryableOperation } from './revisionOperations';

class EditRevisionModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      revision: {
        comment: ""
      }
    };

    this.form = React.createRef();
  }

  onEnter = () => {
    this.setState({
      revision: {
        comment: this.props.revision.comment
      }
    });
  }

  handleEdit = () => {
    // Check form validity
    if ( !this.form.current.reportValidity() ) {
      return;
    }

    // Initiate processing
    const revision = {
      comment: this.state.revision.comment
    };

    this.props.handleEdit(revision);

    // Close modal
    this.props.onHide();
  }

  render() {
    return (
      <Modal
        onEnter={this.onEnter}
        onHide={this.props.onHide}
        show={this.props.show}
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Revision
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form ref={this.form}>
            <Form.Group>
              <Form.Label>
                <strong>Comment</strong>
              </Form.Label>
              <Row>
                <Col sm={12}>
                  <Form.Control
                    type="text"
                    size="sm"
                    autoFocus
                    value={this.state.revision.comment}
                    onChange={(event) => {
                      let revision = this.state.revision;
                      revision.comment = event.target.value;
                      this.setState({revision: revision});
                    }}
                  />
                </Col>
              </Row>
              <Form.Text muted>
                Provide comment describing the revision.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={this.props.onHide}>Close</Button>
          <Button variant="success" onClick={this.handleEdit}>Apply</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

// The tab's own reading size -- the server's default page is smaller and meant for a menu, not a
// screen meant to be read.
const REVISIONS_PAGE_LIMIT = 25;

// The row's one in-flight message, chosen by what `running` names.
const OPERATION_MESSAGE = {
  edit: "Updating revision details...",
  commit: "Initiating revision commit...",
  new: "Initiating revision creation...",
  reopen: "Initiating revision reopen...",
  discard: "Initiating revision discard...",
  delete: "Initiating revision deletion...",
  reset: "Initiating revision reset...",
  retry: "Retrying the failed operation..."
};

class RevisionView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      author: null,
      running: null,
      errorMessage: null,
      editModalShow: false,
      setEditModalShow: (show) => {
        this.setState({editModalShow: show});
      },
      // Which confirmation is open: "new" | "commit" | "reopen" | "discard" | "delete" | "reset" |
      // "retry" | null. Only one can be open at a time, so one field stands in for seven booleans.
      confirmOperation: null
    }

    // Guards state writes from a promise that settles after this row is gone -- Discard and Delete
    // both remove the row on success, and their own runOperation continuation is still pending then.
    this.unmounted = false;
  }

  componentDidMount() {
    this.loadAuthor();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  loadAuthor() {
    // Load author
    this.props.app.api.usersIdGet(this.props.revision.user_id)
      .then(response => {
        this.setState({
          author: response.data.user
        });
      }, error => {});
  }

  handleEditInit() {
    this.setState({
      errorMessage: null,
      editModalShow: true
    });
  }

  // One shape for eight operations: the flag, the error and the refresh are the same every time, and a
  // ninth copy of it is how a row ends up disabling the wrong control.
  runOperation(name, call) {
    this.setState({running: name, errorMessage: null});
    return call().then(() => {
      if ( this.unmounted ) return;
      this.setState({running: null});
      this.props.revisionsInterface.afterOperation();
    }, (error) => {
      if ( this.unmounted ) return;
      this.setState({running: null, errorMessage: error.message});
    });
  }

  handleEdit = (revision) => {
    return this.runOperation("edit", () => this.props.app.api.revisionsIdPatch(this.props.revision.id, revision));
  }

  handleCommit(comment) {
    return this.runOperation("commit",
      () => this.props.app.api.revisionsIdCommitPost(this.props.revision.id, comment));
  }

  // Creating a revision is the one operation whose point is to work in what it made, so it moves the
  // page -- through the door, which asks about unsaved work first. Every other operation leaves the
  // page where it is.
  handleNew(comment) {
    const {org_id: orgId, id: blobId} = this.props.blob;
    return this.runOperation("new", () => this.props.app.api.blobsIdRevisionsPost(orgId, blobId, comment)
      .then(response => {
        const created = response.data && response.data.revision_id;
        if ( created ) {
          this.props.revisionsInterface.revisionCreated(created);
        }
        return response;
      }));
  }

  handleReopen() {
    return this.runOperation("reopen", () => this.props.app.api.revisionsIdReopenPost(this.props.revision.id));
  }

  handleDiscard() {
    return this.runOperation("discard", () => this.props.app.api.revisionsIdDelete(this.props.revision.id));
  }

  handleDelete() {
    return this.runOperation("delete", () => this.props.app.api.revisionsIdDelete(this.props.revision.id));
  }

  handleReset() {
    return this.runOperation("reset", () => this.props.app.api.revisionsIdResetPost(this.props.revision.id));
  }

  // Retry re-issues the call the recorded cause names -- discard and delete are the same DELETE, and
  // which word applies is retryableOperation's call, never inferred again here from the phase.
  handleRetry(op) {
    const call = "commit" === op
      ? () => this.props.app.api.revisionsIdCommitPost(this.props.revision.id)
      : () => this.props.app.api.revisionsIdDelete(this.props.revision.id);
    return this.runOperation("retry", call);
  }

  closeConfirm = () => {
    this.setState({confirmOperation: null});
  }

  render() {
    const api = this.props.app.api;
    const revision = this.props.revision;
    const blob = this.props.blob;
    const X = revision.comment;

    // A row's own controls, not the list's: blocked by its own operation, or its own revision not
    // having settled yet -- a sibling mid-commit cannot gate recovery controls on this row.
    const controlsEnabled = !this.state.running && api.hasRevisionReachedTerminalStatus(revision);
    const retryOp = retryableOperation(revision);
    const retryLabel = "Retry " + (retryOp || "commit");

    return (
      <>
        <tr>
          <td>
            <div className="d-flex">
              <div className="flex-grow-1 mr-3">
                {/* The comment names the revision, so it leads with the states that qualify it --
                    phase, default, selected -- on the same line. The id follows on its own. The blob
                    type is gone: identical on every row here and already in blob settings. `parent_id`
                    is read by Discard's gate and not shown; nobody pastes it. */}
                <span className="record-title">
                  <span className="record-title-main">
                    <strong>{revision.comment}</strong>
                    <Badge variant="secondary" className="title-tag-badge">{revision.phase}</Badge>
                    {revision.id === blob.default_revision_id &&
                      <Badge variant="secondary" className="title-tag-badge">default</Badge>
                    }
                    {(this.props.blobRevision && revision.id === this.props.blobRevision.id) &&
                      <Badge variant="secondary" className="title-tag-badge">selected</Badge>
                    }
                  </span>
                  <StatusIndicator status={revision.status}/>
                </span>

                <div className="record-meta">
                  <span className="record-meta-value">
                    <code>{revision.id}</code>
                    <Clipboard text={revision.id}/>
                  </span>
                </div>

                {this.state.author &&
                  <div className="revision-author">
                    <UserIdentity user={this.state.author} fallback={revision.user_id} strong={false}/>
                    &nbsp;&middot;&nbsp;{Utils.formatTimeAgo(revision.created_at)}
                  </div>
                }
              </div>

              <div className="ml-auto">
                {(controlsEnabled && api.canEditRevisionRecord(blob, revision)) &&
                  <Button
                    variant="success"
                    className="btn-icon mr-2"
                    data-operation="edit"
                    onClick={() => this.handleEditInit()}
                    ><GoPencil /> <span>Edit</span></Button>
                }

                {(controlsEnabled && api.canCommitRevision(blob, revision)) &&
                  <Button
                    variant="success"
                    className="btn-icon mr-2"
                    data-operation="commit"
                    onClick={() => this.setState({confirmOperation: "commit"})}
                    ><GoLock /> <span>Commit</span></Button>
                }

                {(controlsEnabled && api.canCreateRevision(blob, revision)) &&
                  <Button
                    variant="success"
                    className="btn-icon mr-2"
                    data-operation="new"
                    onClick={() => this.setState({confirmOperation: "new"})}
                    ><GoGitCommit /> <span>New</span></Button>
                }

                {(controlsEnabled && api.canReopenRevision(blob, revision)) &&
                  <Button
                    variant="success"
                    className="btn-icon mr-2"
                    data-operation="reopen"
                    onClick={() => this.setState({confirmOperation: "reopen"})}
                    ><GoUnlock /> <span>Reopen</span></Button>
                }

                {(controlsEnabled && api.canResetRevision(blob, revision)) &&
                  <Button
                    variant="success"
                    className="btn-icon mr-2"
                    data-operation="reset"
                    onClick={() => this.setState({confirmOperation: "reset"})}
                    ><GoSync /> <span>Reset</span></Button>
                }

                {/* Retry's role follows the call it re-issues, not the rows it appears on: a retried
                    discard or delete is the admin-only DELETE, where Reset's write gate would offer it
                    to a member the API refuses. */}
                {(controlsEnabled && retryOp && api.canRetryRevision(blob, revision, retryOp)) &&
                  <Button
                    variant="success"
                    className="btn-icon mr-2"
                    data-operation="retry"
                    onClick={() => this.setState({confirmOperation: "retry"})}
                    ><GoHistory /> <span>Retry {retryOp}</span></Button>
                }

                {(controlsEnabled && api.canDiscardRevision(blob, revision)) &&
                  <Button
                    variant="danger"
                    className="btn-icon mr-2"
                    data-operation="discard"
                    onClick={() => this.setState({confirmOperation: "discard"})}
                    ><GoX /> <span>Discard</span></Button>
                }

                {(controlsEnabled && api.canDeleteRevision(blob, revision)) &&
                  <Button
                    variant="danger"
                    className="btn-icon"
                    data-operation="delete"
                    onClick={() => this.setState({confirmOperation: "delete"})}
                    ><GoTrash /></Button>
                }
              </div>
            </div>
          </td>
        </tr>
        {this.state.running &&
          <tr>
            <td style={{padding: "0px"}}>
              <div>
                <Alert variant="primary">
                  {OPERATION_MESSAGE[this.state.running]}
                </Alert>
              </div>
            </td>
          </tr>
        }
        {this.state.errorMessage &&
          <tr>
            <td style={{padding: "0px"}}>
              <div>
                <Alert variant="warning">
                  {this.state.errorMessage}
                </Alert>
              </div>
            </td>
          </tr>
        }

        <EditRevisionModal
          show={this.state.editModalShow}
          onHide={() => this.state.setEditModalShow(false)}
          handleEdit={this.handleEdit}
          revision={revision}
        />

        <ConfirmModal
          show={"new" === this.state.confirmOperation}
          onHide={this.closeConfirm}
          onConfirm={(comment) => this.handleNew(comment)}
          title="New revision"
          confirmLabel="Create revision"
          field={{
            label: "Comment",
            required: true,
            placeholder: "What this revision is for",
            note: "Required. It is what the revision is called everywhere else."
          }}
          body={
            <p>
              A new draft starts from <strong>{X}</strong> and becomes this blob's default revision. It
              carries the workflow definitions across; sessions, threads and execution history stay
              behind on {X}.
            </p>
          }
        />

        <ConfirmModal
          show={"commit" === this.state.confirmOperation}
          onHide={this.closeConfirm}
          onConfirm={(comment) => this.handleCommit(comment)}
          title="Commit revision"
          confirmLabel="Commit"
          field={{
            label: "Comment",
            required: false,
            placeholder: "Leave blank to keep the draft's comment",
            note: "Replaces the draft's comment. It cannot be changed after the commit."
          }}
          body={
            <>
              <p>
                Committing <strong>{X}</strong> closes every session open on it and stops the executions
                they are running. The revision then becomes read-only — its content and its runtime are
                both frozen.
              </p>
              <p>Reopen brings back writes later, but not the sessions: the ones closed here stay closed.</p>
            </>
          }
        />

        <ConfirmModal
          show={"reopen" === this.state.confirmOperation}
          onHide={this.closeConfirm}
          onConfirm={() => this.handleReopen()}
          title="Reopen revision"
          confirmLabel="Reopen"
          body={
            <>
              <p>
                Reopening <strong>{X}</strong> returns it to a draft, so its definitions can be edited
                again and new sessions can run on it.
              </p>
              <p>The sessions its commit closed stay closed. Reopening restores writes, not runtime.</p>
            </>
          }
        />

        <ConfirmModal
          show={"discard" === this.state.confirmOperation}
          onHide={this.closeConfirm}
          onConfirm={() => this.handleDiscard()}
          title="Discard draft"
          confirmLabel="Discard draft"
          body={
            <>
              <p>
                Discarding <strong>{X}</strong> deletes the draft and everything in it — its sessions,
                threads and executions — and cannot be undone.
              </p>
              <p>
                This blob's default revision goes back to <strong>the parent, or the newest committed
                revision</strong>.
              </p>
            </>
          }
        />

        <ConfirmModal
          show={"delete" === this.state.confirmOperation}
          onHide={this.closeConfirm}
          onConfirm={() => this.handleDelete()}
          title="Delete revision"
          confirmLabel="Delete revision"
          body={
            <>
              <p>
                Deleting <strong>{X}</strong> removes the revision and everything in it — its definitions,
                sessions and execution history — and cannot be undone.
              </p>
              <p>Any schedule pinned to this revision fails on its next run and records the failure.</p>
            </>
          }
        />

        <ConfirmModal
          show={"reset" === this.state.confirmOperation}
          onHide={this.closeConfirm}
          onConfirm={() => this.handleReset()}
          title="Reset revision"
          confirmLabel="Reset"
          body={
            <>
              <p>Reset clears the failure on <strong>{X}</strong> and returns it to ready.</p>
              <p>
                It does not retry what failed. Once the revision is ready you can commit it, discard it
                or delete it as usual.
              </p>
            </>
          }
        />

        <ConfirmModal
          show={"retry" === this.state.confirmOperation}
          onHide={this.closeConfirm}
          onConfirm={() => this.handleRetry(retryOp)}
          title={retryLabel}
          confirmLabel={retryLabel}
          body={
            <>
              <p>
                The last {retryOp || "commit"} of <strong>{X}</strong> failed. Retrying runs it again from
                the start.
              </p>
              {"commit" === retryOp &&
                <p>
                  Any session still open on the revision is closed, and the executions it is running are
                  stopped.
                </p>
              }
            </>
          }
        />
      </>
    );
  }
}

export default class BlobRevisions extends Component {
  constructor(props) {
    super(props);

    this.state = {
      refresh: this.refresh,
      afterOperation: this.afterOperation,
      revisionCreated: this.revisionCreated,
      revisions: null,
      cursor: null,
      loadingMore: false,
      allRevisionsInTerminalStatus: true
    };

    // Guards a promise settling after this list is gone -- without it, a resolved fetch or a
    // leaked poll would setState (in a test, into a torn-down reset mock) once the reader has
    // navigated away. this.pollTimer is the poll's own handle, cleared on unmount below; the guard
    // stays as a second layer for whatever is already in flight when unmount runs, the same
    // discipline debugger/session's sessionPollTimer follows.
    this.unmounted = false;
    this.pollTimer = null;
  }

  componentDidMount() {
    this.refresh(false);
  }

  componentWillUnmount() {
    this.unmounted = true;
    if ( this.pollTimer ) clearTimeout(this.pollTimer);
  }

  // The first page, always -- an operation changes what the list contains, so it drops whatever
  // Load More accumulated and refetches from the top rather than patching it. Same rule the thread
  // view follows for a subject that changed.
  revisionCreated = (revisionId) => {
    if ( this.props.onRevisionCreated ) {
      this.props.onRevisionCreated(revisionId);
    }
  }

  // An operation changes this list AND the header selector's own, which is a separate fetch made at
  // most once per page load -- so the page is told to drop it rather than leave the menu stale.
  afterOperation = () => {
    this.refresh(true);
    if ( this.props.onRevisionsChanged ) {
      this.props.onRevisionsChanged();
    }
  }

  refresh = (forcedBlobRefresh) => {
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;

    this.props.app.api.blobsIdRevisionsGet(orgId, blobId, { limit: REVISIONS_PAGE_LIMIT })
      .then(response => {
        if ( this.unmounted ) return;
        const revisions = response.data.revisions;
        const cursor = response.data.cursor || null;

        // Look for revisions that are being transitioned into a different state
        let allRevisionsInTerminalStatus = true;
        for ( let revision of revisions ) {
          if ( !this.props.app.api.hasRevisionReachedTerminalStatus(revision) ) {
            allRevisionsInTerminalStatus = false;
            break;
          }
        }

        // Schedule refresh if there are in-flight revisions
        if ( !allRevisionsInTerminalStatus ) {
          if ( this.pollTimer ) clearTimeout(this.pollTimer);
          this.pollTimer = setTimeout(() => {
            this.pollTimer = null;
            if ( this.unmounted ) return;
            this.refresh(forcedBlobRefresh);
          }, 3000);
        }

        // Remember data
        const prevAllRevisionsInTerminalStatus = this.state.allRevisionsInTerminalStatus;
        this.setState({
          revisions: revisions,
          cursor: cursor,
          allRevisionsInTerminalStatus: allRevisionsInTerminalStatus
        });

        // Refresh latest blob revision when transitioning back to steady state
        if ( forcedBlobRefresh || (allRevisionsInTerminalStatus && !prevAllRevisionsInTerminalStatus) ) {
          this.props.blobInterface.loadBlob();
        }
      }, error => {
        if ( this.unmounted ) return;
        // Remember data
        this.setState({
          revisions: null,
          cursor: null
        });
      });
  }

  // Appends the next page onto what is already on screen -- only refresh() ever resets it.
  loadMore = () => {
    if ( !this.state.cursor || this.state.loadingMore ) {
      return;
    }

    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    const cursor = this.state.cursor;

    this.setState({ loadingMore: true });
    this.props.app.api.blobsIdRevisionsGet(orgId, blobId, { limit: REVISIONS_PAGE_LIMIT, cursor })
      .then(response => {
        if ( this.unmounted ) return;
        this.setState(state => ({
          revisions: [...(state.revisions || []), ...response.data.revisions],
          cursor: response.data.cursor || null,
          loadingMore: false
        }));
      }, error => {
        if ( this.unmounted ) return;
        this.setState({ loadingMore: false });
      });
  }

  render() {
    if ( null == this.state.revisions ) {
      return (
        <>
          <Container className="blob-header-row content-row">
            <Alert variant="primary">Loading revisions...</Alert>
          </Container>
        </>
      );
    }

    return (
      <>
        <Container className="blob-header-row content-row">
          {this.state.revisions && 0 != this.state.revisions.length &&
            <Table hover bordered>
              <tbody>
                {this.state.revisions.map((revision) => {
                  return (
                    <RevisionView
                      key={revision.id}
                      app={this.props.app}
                      blob={this.props.blob}
                      blobRevision={this.props.revision}
                      match={this.props.match}
                      revisionsInterface={this.state}
                      revision={revision}
                    />
                  );
                })}
              </tbody>
            </Table>
          }
          {this.state.revisions && 0 == this.state.revisions.length &&
            <>
              No revisions found for the blob
            </>
          }
          {this.state.cursor &&
            <div className="text-center">
              <Button variant="light" size="sm" disabled={this.state.loadingMore} onClick={this.loadMore}>
                {this.state.loadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          }
        </Container>
      </>
    );
  }
}
